import clsx from 'clsx';
import React, { useMemo } from 'react';
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd';

interface AvoDndProps<T> extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  droppableId?: string;
  items: T[];
  children: (params: { items: T[] }) => React.ReactNode;
  onChange: (items: T[]) => void;
}

export const AvoDnd = <T,>({
  children,
  droppableId: propDroppableId,
  items,
  className,
  onChange,
  ...rest
}: AvoDndProps<T>) => {
  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;
    if (source.droppableId !== destination.droppableId) return;
    if (source.index === destination.index) return;
    const reorderedItems = Array.from(items);
    const [removed] = reorderedItems.splice(source.index, 1);
    reorderedItems.splice(destination.index, 0, removed);
    onChange(reorderedItems);
  };

  const generatedDroppableId = useMemo(() => Math.random().toString(36).substr(2, 9), []);
  const droppableId = propDroppableId || generatedDroppableId;

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId={droppableId}>
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={clsx('h-full', className)}
            {...rest}
          >
            {children({ items })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

interface AvoDndItemProps extends React.HTMLAttributes<HTMLDivElement> {
  itemId: string;
  index: number;
  isDragDisabled?: boolean;
}

export const AvoDndItem: React.FC<AvoDndItemProps> = ({
  isDragDisabled,
  itemId,
  index,
  children,
  ...rest
}) => {
  return (
    <Draggable draggableId={itemId} index={index} isDragDisabled={isDragDisabled}>
      {(provided) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={{
            ...provided.draggableProps.style,
            top: 'auto',
            left: 'auto',
            /**
             * this auto position is required because of choppy position when dragging.
             * https://stackoverflow.com/questions/54982182/react-beautiful-dnd-drag-out-of-position-problem
             * https://github.com/atlassian/react-beautiful-dnd/issues/485#issuecomment-385816391
             * Overriding top, left is fixing the issue.
             */
          }}
          {...rest}
        >
          {children}
        </div>
      )}
    </Draggable>
  );
};
